import { Component, type ReactNode, type ErrorInfo } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import type { Translate } from '@resource-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '../primitives/Card';
import { Button } from '../primitives/Button';
import { AlertTriangle } from 'lucide-react';

interface Props {
  /**
   * The kit's translate seam, threaded in as a REQUIRED prop.
   *
   * An error boundary has to be a class (only a class can implement
   * `getDerivedStateFromError`), and a class cannot call a hook — so the
   * capability is handed to it by the function component below, which can.
   * That is the same job the retired translation HOC did, minus the direct
   * dependency on the host's i18n library.
   */
  t: Translate;
  children: ReactNode;
  fallbackTitle?: string;
  // Invoked when the operator clicks "Try Again", BEFORE the boundary drops
  // its error state. ResourcePage wires this to RESET (evict) the underlying
  // resource query so a render error rooted in bad cached data can actually
  // clear: the child remounts into a loading state and refetches, instead of
  // immediately re-rendering the same bad data and re-throwing. Merely
  // invalidating would only mark the data stale (no active observer while the
  // error card is shown → no refetch), so the first retry would re-throw.
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ResourceErrorBoundaryImpl extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ResourceErrorBoundary]', error, errorInfo);
  }

  handleRetry = () => {
    this.props.onRetry?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    // Read from PROPS on every render, never cached in state or the
    // constructor: the injected `t` changes identity on a language switch, so
    // reading it here is what makes the fallback copy re-translate while the
    // error card is still on screen.
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {this.props.fallbackTitle || t('rk.resource.somethingWrong')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('rk.resource.unexpectedError')}</p>
            {this.state.error && (
              <pre className="text-xs text-destructive bg-muted/50 p-3 rounded-md overflow-auto">
                {this.state.error.message}
              </pre>
            )}
            <Button variant="outline" size="sm" onClick={this.handleRetry}>
              {t('rk.resource.tryAgain')}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

/**
 * The exported boundary. A function component purely so it can read the seam
 * and hand `t` down; it holds no state and adds no behaviour of its own.
 */
export function ResourceErrorBoundary(props: Omit<Props, 't'>) {
  const { t } = useResourceKit();
  return <ResourceErrorBoundaryImpl {...props} t={t} />;
}
